import { useEffect, useMemo, useRef, useState } from 'react';
import type { MeResponse, ProductListItem, StockCountCard, StockCountRef, StockWarehouseRef } from '@kassa/shared';
import { api, ApiError } from '../../api';
import { formatQty, hasPermission, UNIT_LABEL } from '../../format';
import type { QueueItem } from '../../offlineQueue';

// Skaner → «Sayım» (KT-108 (6b)): açıq sayıma oxut; hər oxunuş öz UUID-i ilə internetsiz növbəyə düşür (təkrar bir dəfə yazılır).
// Ekrandakı «Sayılan» = serverdəki cəm + cihazda gözləyən oxunuşlar. «Bitir» — növbə boşalandan sonra (bütün oxunuşlar serverdə).

export interface CountScanMeta {
  owner: string;
  countId: string;
  productId: string;
  name: string;
  unit: string;
  delta: number;
}

const errText = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof TypeError ? 'İnternet yoxdur' : e instanceof Error ? e.message : 'Xəta baş verdi');
const q3 = (n: number) => Number(n.toFixed(3));

interface Props {
  me: MeResponse;
  owner: string;
  items: QueueItem[];
  warehouses: StockWarehouseRef[];
  enqueue: (i: QueueItem) => Promise<boolean>;
  flush: () => Promise<void>;
  /** Skaner oxuduğu malı bura ötürür */
  register: (fn: ((p: ProductListItem) => void) | null) => void;
  setMsg: (m: { text: string; bad: boolean } | null) => void;
}

export function CountMode({ me, owner, items, warehouses, enqueue, flush, register, setMsg }: Props) {
  const [counts, setCounts] = useState<StockCountRef[] | null>(null);
  const [active, setActive] = useState<StockCountCard | null>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [current, setCurrent] = useState<{ productId: string; name: string; unit: string } | null>(null);
  const [typed, setTyped] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canApprove = hasPermission(me, 'approve.accounting');

  const loadList = () =>
    api
      .counts()
      .then(setCounts)
      .catch((e: unknown) => setMsg({ text: `Sayımlar yüklənmədi: ${errText(e)}`, bad: true }));
  // Gec gələn köhnə cavab yenisinin üstünə yazılmasın
  const openSeq = useRef(0);
  const open = (id: string) => {
    const my = ++openSeq.current;
    return api
      .countCard(id)
      .then((c) => my === openSeq.current && setActive(c))
      .catch((e: unknown) => setMsg({ text: `Sayım açılmadı: ${errText(e)}`, bad: true }));
  };
  useEffect(() => {
    void loadList();
  }, []);
  useEffect(() => {
    if (!warehouseId && warehouses[0]) setWarehouseId(warehouses[0].id);
  }, [warehouses]);

  // Bu sayımın cihazda gözləyən oxunuşları (rədd olunanlar sayılmır)
  const pendingOf = (countId: string) =>
    items.filter((i) => !i.error && (i.meta as CountScanMeta | undefined)?.countId === countId).map((i) => i.meta as CountScanMeta);
  const pending = active ? pendingOf(active.id) : [];
  // Göndərilən oxunuş növbədən çıxır, serverdə sətrə düşür — sayımı yenidən oxu (yoxsa ekranda say azalır)
  const prevPending = useRef(0);
  useEffect(() => {
    if (active && pending.length < prevPending.current) void open(active.id);
    prevPending.current = pending.length;
  }, [pending.length, active?.id]);

  // Server sətirləri + gözləyən oxunuşlar
  const lines = useMemo(() => {
    const m = new Map<string, { productId: string; name: string; unit: string; counted: number; system: number | null }>();
    for (const l of active?.lines ?? []) m.set(l.productId, { productId: l.productId, name: l.name, unit: l.unitCode, counted: l.counted, system: l.system });
    for (const p of pending) {
      const cur = m.get(p.productId) ?? { productId: p.productId, name: p.name, unit: p.unit, counted: 0, system: null };
      m.set(p.productId, { ...cur, counted: q3(cur.counted + p.delta) });
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, 'az'));
  }, [active, items]);

  const addDelta = async (p: { productId: string; name: string; unit: string }, delta: number) => {
    if (!active || active.status !== 'counting' || delta === 0) return;
    const id = crypto.randomUUID();
    const meta: CountScanMeta = { owner, countId: active.id, productId: p.productId, name: p.name, unit: p.unit, delta };
    const ok = await enqueue({
      id,
      path: `/api/counts/${active.id}/scans`,
      body: { scans: [{ id, productId: p.productId, delta, at: new Date().toISOString() }] },
      createdAt: new Date().toISOString(),
      error: null,
      label: `Sayım: ${p.name} ${delta > 0 ? '+' : ''}${String(delta).replace('.', ',')}`,
      meta,
    });
    if (ok) setCurrent(p);
  };

  // Skanerdən gələn mal: +1 (qutu barkodu — qutudakı say)
  useEffect(() => {
    if (!active || active.status !== 'counting') {
      register(null);
      return;
    }
    register((p) => {
      setTyped(null);
      void addDelta({ productId: p.id, name: p.name, unit: p.unitCode }, p.matchedQty ?? 1);
    });
    return () => register(null);
  }, [active?.id, active?.status, items]);

  const create = async () => {
    if (!warehouseId) return;
    setBusy(true);
    try {
      const c = await api.countCreate({ id: crypto.randomUUID(), warehouseId, note: null });
      await open(c.id);
    } catch (e) {
      // Bu anbarda açıq sayım var (409) — siyahıda görünür, «Aç» ilə qoşulmaq olar
      setMsg({ text: errText(e), bad: true });
      await loadList();
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    if (!active) return;
    setBusy(true);
    try {
      await fn();
      setMsg({ text: ok, bad: false });
      await open(active.id);
      await loadList();
    } catch (e) {
      setMsg({ text: errText(e), bad: true });
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!active) return;
    await flush();
    if (pendingOf(active.id).length > 0) {
      setMsg({ text: 'Oxunuşlar hələ serverə getməyib — internet gələndə yenidən «Bitir» basın', bad: true });
      return;
    }
    await act(() => api.countSubmit(active.id), 'Sayım təsdiqə göndərildi');
  };

  if (!active) {
    const openCounts = (counts ?? []).filter((c) => c.status === 'counting' || c.status === 'submitted');
    return (
      <section className="card scanner-card" aria-label="Sayım">
        {counts === null && <p className="muted">Yüklənir…</p>}
        {openCounts.length > 0 && (
          <ul className="scanner-stock">
            {openCounts.map((c) => (
              <li key={c.id}>
                <span>
                  {c.warehouseName} · {c.status === 'counting' ? 'sayılır' : 'təsdiq gözləyir'} · {c.counted} mal
                </span>
                <button type="button" className="btn-small" onClick={() => void open(c.id)}>
                  Aç
                </button>
              </li>
            ))}
          </ul>
        )}
        <label className="field">
          <span>Anbar</span>
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary scanner-wide" disabled={busy || !warehouseId} onClick={() => void create()}>
          Yeni sayım
        </button>
      </section>
    );
  }

  const cur = current ? lines.find((l) => l.productId === current.productId) : null;
  const counting = active.status === 'counting';
  return (
    <section className="card scanner-card" aria-label="Sayım">
      <div className="scanner-count-head">
        <div>
          <strong>Sayım · {active.warehouseName}</strong>
          <span className="muted small">{active.status === 'counting' ? 'sayılır' : active.status === 'submitted' ? 'təsdiq gözləyir' : active.status === 'approved' ? `təsdiqləndi ${active.docNumber ?? ''}` : 'ləğv edilib'}</span>
        </div>
        <button type="button" className="btn-small" onClick={() => setActive(null)}>
          Siyahı
        </button>
      </div>
      {pending.length > 0 && <p className="chip-warn">İnternetsiz · {pending.length} oxunuş növbədə</p>}

      {counting && cur && (
        <div className="scanner-now" aria-label="İndi oxundu">
          <p className="muted small">İNDİ OXUNDU</p>
          <h3>{cur.name}</h3>
          <div className="scanner-stepper scanner-stepper-big">
            <button type="button" aria-label="Bir az" onClick={() => void addDelta(cur, -1)} disabled={cur.counted < 1}>
              −
            </button>
            <input
              aria-label="Sayılan"
              inputMode="decimal"
              value={typed ?? String(cur.counted).replace('.', ',')}
              onChange={(e) => {
                if (/^\d{0,6}([.,]\d{0,3})?$/.test(e.target.value)) setTyped(e.target.value);
              }}
              onBlur={() => {
                if (typed === null) return;
                const n = Number(typed.replace(',', '.'));
                setTyped(null);
                if (typed !== '' && Number.isFinite(n)) void addDelta(cur, q3(n - cur.counted)); // yazılan say — fərq oxunuş kimi
              }}
            />
            <button type="button" aria-label="Bir artıq" onClick={() => void addDelta(cur, 1)}>
              +
            </button>
          </div>
          <p className="small">
            Sistemdə: {cur.system === null ? '—' : `${formatQty(cur.system)} ${UNIT_LABEL[cur.unit] ?? cur.unit}`} · Fərq:{' '}
            {cur.system === null ? '—' : String(q3(cur.counted - cur.system)).replace('.', ',')}
          </p>
        </div>
      )}

      <div className="table-wrap">
        <table className="data-table" aria-label="Sayım sətirləri">
          <thead>
            <tr>
              <th scope="col">Mal</th>
              <th scope="col" className="num">
                Sayılan
              </th>
              <th scope="col" className="num">
                Sistem
              </th>
              <th scope="col" className="num">
                Fərq
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const d = l.system === null ? null : q3(l.counted - l.system);
              return (
                <tr key={l.productId} onClick={() => counting && setCurrent(l)}>
                  <td>{l.name}</td>
                  <td className="num mono">{formatQty(l.counted)}</td>
                  <td className="num mono">{l.system === null ? '—' : formatQty(l.system)}</td>
                  <td className="num mono">
                    {d === null ? '—' : d === 0 ? <span className="badge badge-ok">uyğun ✓</span> : <span className={d < 0 ? 'diff-minus' : 'diff-plus'}>{d > 0 ? `+${formatQty(d)}` : formatQty(d)}</span>}
                  </td>
                </tr>
              );
            })}
            {lines.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  Malı oxudun — hər oxunuş +1
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="scanner-actions">
        {counting && (
          <button type="button" className="btn-primary" disabled={busy || lines.length === 0} onClick={() => void submit()}>
            Bitir və təsdiqə göndər
          </button>
        )}
        {active.status === 'submitted' && canApprove && (
          <button type="button" className="btn-primary" disabled={busy} onClick={() => window.confirm('Fərqlər qalığa yazılsın (SAY sənədi)?') && void act(() => api.countApprove(active.id), 'Təsdiqləndi — düzəliş sənədi yarandı')}>
            Təsdiqlə
          </button>
        )}
        {active.status === 'submitted' && (
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => void act(() => api.countReopen(active.id), 'Sayım yenidən açıldı')}>
            Geri aç
          </button>
        )}
        {(counting || active.status === 'submitted') && (
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => window.confirm('Sayım ləğv edilsin?') && void act(() => api.countCancel(active.id), 'Sayım ləğv edildi')}>
            Ləğv et
          </button>
        )}
      </div>
      <p className="muted small">İnternet gələndə oxunuşlar özü göndərilir. Düzəliş sənədi təsdiqdən sonra yaranır.</p>
    </section>
  );
}
